import { SectionHandler, SectionHandlerGroup } from '../entity.handler';
import { AdminEditorService } from '../../admin.service';
import { AnalyticsCellValue, AnalyticsSettings } from 'app/shared/components/analytics';
import { StatsEntityOutputOptions } from '@key-telematics/fleet-api-client';
import objectHash from 'object-hash';
import { EventEmitter } from '@angular/core';

export interface ListSectionValues {
    items: any;
}


export function buildChartSectionGroup(admin: AdminEditorService, entityType: string, metrics: string[], ranges: string[]): SectionHandlerGroup<any> {

    let currentRange = admin.cache['chart-date-range'] || '7d';
    if (!ranges.includes(currentRange)) {
        currentRange = ranges[0];
    }

    const charts = metrics.map(metric => {
        return new ChartSectionHandler(admin, entityType, admin.i18n.instant(`ADMIN.EDITORS.SHARED.CHART.TITLES.${metric.replace(/-/g, '_').toUpperCase()}S`), `${metric}s`, currentRange);
    });

    const actions = {
        id: 'range',
        icon: 'date',
        menu: ranges.map(range => ({
            id: range,
            type: 'radio',
            checked: range === currentRange,
            closeDropdownOnClicked: true,
            text: admin.i18n.instant('ADMIN.EDITORS.SHARED.CHART.RANGES.' + range.toUpperCase()),
            action: () => {
                actions.menu.forEach(item => {
                    item.checked = item.id === range;
                });
                admin.cache['chart-date-range'] = range;
                charts.forEach(chart => chart.setDateRange(range));
            },
        })),
    };

    return {
        name: admin.i18n.instant('ADMIN.EDITOR.STATS'),
        actions: [actions],
        sections: charts,
    };
}


export class ChartSectionHandler implements SectionHandler<any, ListSectionValues> {

    type = 'chart';
    icon = '';
    bulk: false;
    description = '';

    onSettingsChanged = new EventEmitter<void>();

    constructor(
        public admin: AdminEditorService,
        private entityType: string,
        public title: string,
        private metric: string,
        private dateRange: string
    ) {
    }

    applyUpdate(_entity: any, _payload: any): Promise<any> {
        return Promise.reject(new Error('applyUpdate not supported'));
    }

    mapEntityToEditor(_entity: any): Promise<ListSectionValues> {
        return Promise.reject(new Error('mapEntityToEditor not supported'));
    }

    mapEditorToEntity(_entity: any, _values: ListSectionValues): Promise<any> {
        return Promise.reject(new Error('mapEditorToEntity not supported'));
    }

    setDateRange(range: string) {
        this.dateRange = range;
        this.onSettingsChanged.emit();
    }

    async loadDataSet(entity: any): Promise<{ chartType: string, cellset: AnalyticsCellValue[][], settings: AnalyticsSettings }> {

        const rowLevel = this.dateRange.endsWith('h') ? 'hour' : 'day';

        const options: StatsEntityOutputOptions = {
            groupLevel: 'all',
            rowLevel: rowLevel,
            flip: false,
            time: this.dateRange,
        };

        const result = await this.admin.getCached(`stats/getOutputForEntity/${this.entityType}/${entity.id}/${this.metric}/${objectHash(options)}`, api => api.stats.getOutputForEntity(this.entityType as any, entity.id, this.metric as any, options));

        const chartType = ['records', 'connections', 'bytes', 'minutes'].includes(this.metric) ? 'bar' : 'line';

        const settings: Pick<AnalyticsSettings, 'table' | 'graph'> = {
            table: {
                measures: [{
                    generic: true,
                    key: 'count',
                    value: 'Count',
                }],
                layout: 'data',
            },
            graph: {
                xAxis: {
                    labels: {
                        layout: 'horizontal',
                    },
                },
                yAxis: {
                    left: {
                        max: this.metric === 'minutes' && rowLevel === 'day' ? '86400' : undefined, // make sure time graphs don't exceed 24 hours, it looks weird
                    },
                    right: {},
                },
                stacked: false,
                dataLabels: false,
                label: {
                    visible: false,
                    placement: 'asLegend',
                    position: 'bottom',
                    overlay: false,
                },
                series: {
                    default: {
                        visible: true,
                        type: chartType,
                        orientation: 'vertical',
                        fill: true,
                        color: null,
                        yAxis: 'left',
                        average: false,
                    },
                },
                seriesColors: {
                    count: '#69aaf8', // this.admin.primaryColor
                },
            },
        };

        return {
            chartType: 'chart',
            cellset: (result as any).cellset.cellset,
            settings: settings as any,
        };



    }

}

